import React, { createContext, useState, useEffect, useCallback } from "react";

import {
  getSessionData,
  setSessionData,
} from "../../utils/interceptors/localStorageService";
import { postAuthenticatedRequest } from "../../utils/api/requests/request";
import { useNotification } from "./notification-context";
import { useApiRequests } from "../../utils/api/https/executor";

export const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const { triggerNotification } = useNotification();
  const { fetchPoliciesData } = useApiRequests();
  const [selectedFilter, setSelectedFilter] = useState("deposits");
  const [savedSessionData, setSavedSessionData] = useState(null);
  const [bankingProfile, setBankingProfile] = useState([]);
  const [depositPolicies, setDepositPolicies] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const encryptedSessionData =
    typeof window !== "undefined" ? localStorage.getItem("sessionData") : null;

  // Handle fetching policies from storage or API
  // const fetchPolicies = async () => {
  //     if (started) return; // Prevent multiple simultaneous calls

  //     try {
  //         setStarted(true);
  //         setLoading(true);
  //         setError("");
  //         const storedEncryptedPolicies = localStorage.getItem('policiesData');

  //         if (storedEncryptedPolicies) {
  //             const decryptedPolicies = decryptData(storedEncryptedPolicies);
  //             setPolicies(decryptedPolicies.policies);
  //             setPendingQuotes(decryptedPolicies.pendingQuotes);

  //         } else {

  //             const res = await fetchPoliciesData();
  //             const policies = processPolicies(res);
  //             const pendingQuotes = processPending(res);
  //             const encryptedPolicies = encryptData({ policies, pendingQuotes });

  //             localStorage.setItem('policiesData', encryptedPolicies);
  //             console.log("policies... ", policies);
  //             console.log("pendingQuotes... ", pendingQuotes);
  //             setPolicies(policies);
  //             setPendingQuotes(pendingQuotes);
  //         }
  //     } catch (err) {
  //         setError(err.message);
  //         setPolicies([]);
  //         setPendingQuotes([]);

  //     } finally {
  //         setLoading(false);
  //         setStarted(false);
  //     }
  // };

  // Process policies data to categorize and add types
  const processPolicies = (res) => {
    const activePolicyNumbers = new Set(
      [...(res?.upcomingRenewals ?? []), ...(res?.activePolicies ?? [])].map(
        (policy) => policy.policyNumber
      )
    );

    return [
      ...(res?.upcomingRenewals ?? []).map((policy) =>
        categorizePolicy(policy, "renewal")
      ),
      ...(res?.activePolicies ?? []).map((policy) =>
        categorizePolicy(policy, "live")
      ),
      ...(res?.expiredPolicies ?? []).map((policy) =>
        categorizePolicy(policy, "expired")
      ),
      ...(res?.quotes ?? []).map((quote) => categorizePolicy(quote, "quote")),
      ...(res?.depositAccounts ?? []).map((deposit) =>
        categorizePolicy(deposit, "deposit")
      ),
      ...(res?.loanAccounts ?? []).map((loan) =>
        categorizePolicy(loan, "loan")
      ),
    ].map((policy) => {
      let type = "";
      if ((policy.policyName ?? "").includes("Insurance")) type = "insurance";
      else if ((policy.category ?? "").includes("deposit")) type = "deposits";
      else if ((policy.category ?? "").includes("loan")) type = "loans";

      return {
        ...policy,
        type: type,
        active: activePolicyNumbers.has(policy.policyNumber),
      };
    });
  };

  const processPending = (res) => {
    return [
      ...(res.pendingLoanAccounts ?? []).map((policy) =>
        categorizePolicy(policy, "pendingLoans")
      ),
    ];
  };

  const fetchPolicies = useCallback(async () => {
    if (loading) return; // Prevent multiple calls while loading

    try {
      setLoading(true);
      setError("");

      const res = await fetchPoliciesData();
      const processedPolicies = processPolicies(res);
      const processedPendingQuotes = processPending(res);

      console.log("policies... ", processedPolicies);
      console.log("pendingQuotes... ", processedPendingQuotes);

      setBankingProfile(res);
      setPolicies(processedPolicies);
      setPendingQuotes(processedPendingQuotes);
    } catch (err) {
      setError(err.message);
      setPolicies([]);
      setPendingQuotes([]);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [loading, fetchPoliciesData]);

  const getUserData = async () => {
    try {
      const response = await postAuthenticatedRequest("UserProfileQuery", {
        operation: "POST",
      });

      const result = response?.outData;
      if (result?.isSuccessful) {
        const userData = result?.data;
        // Read latest encrypted session at call time to avoid stale merges.
        const currentSessionData = getSessionData() || {};
        const newSessionData = {
          ...currentSessionData,
          ...userData,
          mobileCountryCode: userData.mobileCountryCode,
          mobileNumber: userData.mobileNumber,
          emailAddress: userData.emailAddress,
          // Preserve liveness completion if already true in current session,
          // unless backend explicitly returns true.
          hasCompletedLivenessCheck:
            userData?.hasCompletedLivenessCheck ??
            currentSessionData?.hasCompletedLivenessCheck,
          livenessCheckDate:
            userData?.livenessCheckDate ?? currentSessionData?.livenessCheckDate,
        };
        setSessionData(newSessionData);
        setSavedSessionData(newSessionData);
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error.message,
      });
    }
  };

  // Helper function to categorize policies
  const categorizePolicy = (policy, category) => ({
    ...policy,
    category: category,
    sliderValue: policy.percentageCompletion,
  });

  // Keep savedSessionData synchronized with the encrypted session payload.
  // This prevents stale values (e.g. hasCompletedLivenessCheck) after re-login.
  useEffect(() => {
    const latestSessionData = getSessionData();
    setSavedSessionData(latestSessionData || null);
  }, [encryptedSessionData]);

  useEffect(() => {
    const sessionData = getSessionData();
    if (!initialized && !loading && !!sessionData) {
      fetchPolicies();
    }
  }, [initialized, loading, fetchPolicies, encryptedSessionData]);

  // Fetch policies on mount or when policies change
  useEffect(() => {
    if (policies.length > 0) {
      const filteredDeposits = policies.filter(
        (policy) => policy.type === "deposits"
      );
      setDepositPolicies(filteredDeposits);
    }
  }, [policies]);

  return (
    <DashboardContext.Provider
      value={{
        selectedFilter,
        setSelectedFilter,
        savedSessionData,
        bankingProfile,
        depositPolicies,
        policies,
        pendingQuotes,
        getUserData,
        loading,
        error,
        fetchPolicies,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = React.useContext(DashboardContext);
  if (context === undefined) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};
